import React from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVFormControl,
  EVGrid,
  EVMenuItem,
  EVNavigateNextOutlinedIcon,
  EVSelect,
  EVTypography,
} from 'lib/components';
import clsx from 'lib/clsx';
import { useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import { getEstimatedTotalText, getFormatedDeliveryOption, getAdjustedProductPrice } from 'features/order/Order.utils';
import { PRODUCT_IDS } from 'constants/AppConstants';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC } from 'constants/FeatureFlag.constants';
import useMediaQuery from 'hooks/useMediaQuery';
import { isEntitledForHidePricingSelector, featureFlagSelector } from 'utils/appSelectors.utils';
import { gainsightPxCustomEvent } from 'utils/gainsightPx.utils';
import { GAINSIGHT_PX_EVENT_PRODUCT_TOOLTIP_OPENED } from 'constants/gainsightPxCustomEvents.constants';
import { getTooltipForProduct } from './ProductList.util';
import ProductTooltip from './ProductTooltip.component';
import ThreeHourDeliveryMessage from './ThreeHourDeliveryMessage.component';
import ProductStyles from './ProductStyles';
import { ESX_COMPATIBLE_PRODUCTS } from './ProductList.constants';

function ProductListTemplate(props) {
  const {
    products,
    threeHourDeliverySelected,
    getSelectedDeliveryProduct,
    handleProductSelect,
    handleDeliveryProductSelect,
    getProductSelectComponent,
    anyProductsSelected,
    isDisabled,
  } = props;

  const {
    currencyTypeId,
    selectedPrimaryProduct,
    selectedWallsProduct,
    selectedWallsDeliveryProduct,
    isBillingAffiliateUser,
  } = useSelector(({
    order, userData: { initParameters },
  }) => ({
    ...order,
    currencyTypeId: initParameters?.currencyTypeId,
    isBillingAffiliateUser: initParameters?.isBillingAffiliate,
  }));
  const isESXEnabled = useSelector(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));
  const classes = ProductStyles();
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);

  return (
    <>
      {products.map((product) => {
        // #genai - Special handling for ESX - use static compatibility array
        // ESX is enabled if ANY compatible product (primary OR walls) is selected
        let isIncompatible;
        if (product.ProductID === PRODUCT_IDS.ESX) {
          isIncompatible = !(
            isESXEnabled && (
              (selectedPrimaryProduct && ESX_COMPATIBLE_PRODUCTS.includes(selectedPrimaryProduct.ProductID))
              || (selectedWallsProduct && ESX_COMPATIBLE_PRODUCTS.includes(selectedWallsProduct.ProductID))
            )
          );
        } else {
          isIncompatible = selectedPrimaryProduct
            && !product.CompatibleProducts.includes(selectedPrimaryProduct.ProductID);
        }
        const isProductDisabled = isIncompatible || isDisabled;
        // #genai - Resolve tooltip text without nested conditional expressions.
        let tooltipTitle = product.ProductID === PRODUCT_IDS.PropertyData
          ? ''
          : (getTooltipForProduct(product.ProductID) ?? '');
        if (isIncompatible) {
          tooltipTitle = product.ProductID === PRODUCT_IDS.ESX
            ? 'ESX requires a compatible product to be selected (Roof3D or FullHouse)'
            : `${product.Description} is not compatible with ${
              selectedPrimaryProduct?.Description || selectedWallsProduct?.Description || 'selected product'
            }`;
        }
        return (
          <EVGrid
            key={product.ProductID}
            container
            spacing={0}
            className={clsx('products', isProductDisabled && 'disabled')}
            id={product.ProductID}
          >
            <ProductTooltip
              title={tooltipTitle}
              disableHoverListener={isMobile || product.ProductID === PRODUCT_IDS.PropertyData}
              disableTouchListener={isMobile || product.ProductID === PRODUCT_IDS.PropertyData}
              onOpen={() => gainsightPxCustomEvent(GAINSIGHT_PX_EVENT_PRODUCT_TOOLTIP_OPENED, { productId: product.ProductID })}
            >
              <EVGrid container>
                <EVGrid>
                  {getProductSelectComponent(product.ProductID, isProductDisabled, () => (isProductDisabled || handleProductSelect(product)))}
                </EVGrid>
                <EVGrid component="label" htmlFor={`products-${product.ProductID}`} container justifyContent="space-between" style={{ flex: 1 }}>
                  <EVTypography>
                    {product.Description}
                  </EVTypography>
                  {!isBillingAffiliateUser && !isEntitledForHidePricing && (
                  <EVTypography>
                    {(() => {
                      // #genai - Get adjusted product pricing (e.g., ESX doubles when FullHouse is selected)
                      const { priceMin, priceMax } = getAdjustedProductPrice(product, selectedPrimaryProduct, isESXEnabled);
                      return getEstimatedTotalText([priceMin, priceMax], currencyTypeId);
                    })()}
                  </EVTypography>
                  )}
                </EVGrid>
              </EVGrid>
            </ProductTooltip>

            {anyProductsSelected(product.ProductID) && (
            <>
              <EVGrid container spacing={0} id="productDelivery">
                <EVGrid xs item component="label" htmlFor={`delivery-${product.ProductID}`}>
                  <EVTypography>
                    {t('common_delivery')}
                  </EVTypography>
                </EVGrid>
                <EVGrid item>
                  <EVFormControl variant="outlined">
                    <EVSelect
                      id={`delivery-${product.ProductID}`}
                      value={getSelectedDeliveryProduct(product.ProductID)}
                      onChange={(event) => handleDeliveryProductSelect(event.target.value, product.ProductID)}
                      IconComponent={EVNavigateNextOutlinedIcon}
                      className={classes.select}
                    >
                      {product.WebDeliveryProducts.map((deliverProduct) => {
                        // #genai - Compute each delivery label before rendering its menu item.
                        let deliveryLabel = getFormatedDeliveryOption(
                          deliverProduct.Name,
                          deliverProduct.PriceMax,
                          currencyTypeId,
                          product.ProductID,
                        );
                        if (isBillingAffiliateUser || isEntitledForHidePricing) {
                          deliveryLabel = product.ProductID === PRODUCT_IDS.PropertyData
                            && deliverProduct.Name === 'Regular'
                            ? 'Instant'
                            : deliverProduct.Name;
                        }
                        return (
                          <EVMenuItem
                            key={deliverProduct.ProductID}
                            value={deliverProduct.ProductID}
                            disabled={selectedWallsDeliveryProduct !== null}
                          >
                            {deliveryLabel}
                          </EVMenuItem>
                        );
                      })}
                    </EVSelect>
                  </EVFormControl>
                </EVGrid>
              </EVGrid>
              {threeHourDeliverySelected(product.ProductID) && <ThreeHourDeliveryMessage />}
            </>
            )}
            {isMobile && anyProductsSelected(product.ProductID) && product.ProductID !== PRODUCT_IDS.PropertyData && (
            <EVGrid className={classes.tooltip}>
              {getTooltipForProduct(product.ProductID)}
            </EVGrid>
            )}
          </EVGrid>
        );
      })}
    </>
  );
}

ProductListTemplate.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  getSelectedDeliveryProduct: PropTypes.func.isRequired,
  anyProductsSelected: PropTypes.func.isRequired,
  threeHourDeliverySelected: PropTypes.func.isRequired,
  handleProductSelect: PropTypes.func.isRequired,
  handleDeliveryProductSelect: PropTypes.func.isRequired,
  getProductSelectComponent: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

ProductListTemplate.defaultProps = {
  isDisabled: false,
};

export default ProductListTemplate;
